import { supabase } from '@/lib/supabase';
import type { ErpProvider, FlowDirection, FlowOptionValues, StoreProvider } from '@/lib/interfacesCatalog';

export interface InterfaceConnection {
  id: string;
  company_id: string;
  store_provider: StoreProvider | null;
  erp_provider: ErpProvider | null;
  store_integration_id: string | null;
  erp_integration_id: string | null;
  log_mode: 'all' | 'errors_only';
  log_retention_days: 30 | 90 | 365;
  updated_at: string;
}

export interface InterfaceFlow {
  id: string;
  company_id: string;
  connection_id: string;
  flow_key: string;
  is_enabled: boolean;
  direction: FlowDirection;
  options: FlowOptionValues;
  updated_at: string;
}

export interface IntegrationSummary {
  id: string;
  provider_name: string;
  is_active: boolean;
  last_sync_at: string | null;
}

export interface ActivityLogEntry {
  id: string;
  flow_key: string;
  direction: 'store_to_erp' | 'erp_to_store';
  status: 'success' | 'failed' | 'skipped' | 'pending';
  source_ref: string | null;
  target_ref: string | null;
  summary: string | null;
  error_message: string | null;
  attempts: number;
  created_at: string;
}

export interface ErpWarehouse { id: number; name: string }

export type InterfaceOrderStatus = 'processing' | 'processed' | 'pending_manual' | 'cancelled' | 'failed';

export interface InterfaceOrderLine { key: string; sku: string; name: string; quantity: number }
export interface AllocPart { lineKey: string; storageId: number; quantity: number }

export interface InterfaceOrderDocument {
  id: string;
  storage_id: number;
  storage_name: string | null;
  doc_kind: 'invoice' | 'credit';
  external_number: string | null;
  external_url: string | null;
  amount: number | null;
  split_reason: string | null;
  status: 'pending' | 'success' | 'failed';
  error_message: string | null;
  created_at: string;
}

export interface InterfaceOrder {
  id: string;
  shopify_order_id: string;
  order_name: string;
  status: InterfaceOrderStatus;
  reason: string | null;
  order_payload: { lines?: InterfaceOrderLine[] } | null;
  allocation: AllocPart[] | null;
  stock_snapshot: Record<string, Record<string, number>> | null;
  processed_at: string | null;
  created_at: string;
  interface_order_documents: InterfaceOrderDocument[];
}

export interface PreviewDoc { storageId: number; storageName: string; items: { sku: string; name: string; quantity: number }[]; amount: number }

export interface OrderPreview {
  order_name: string;
  created_at: string;
  existing_status: InterfaceOrderStatus | null;
  trigger_ready: boolean;
  outcome: 'stop' | 'issue';
  split_reason: string | null;
  shortage: { sku: string; name: string; missing: number }[];
  docs: PreviewDoc[];
  suggestion: PreviewDoc[];
}

export interface StockSourceRow {
  item_id: number;
  item_name: string;
  storage_id: number;
  storage_name: string;
  quantity: number;
  counted: boolean;
}

export interface StockComparison {
  sku: string;
  title: string;
  rivhit: StockSourceRow[];
  rivhit_total: number;
  from: number | null;
  to: number;
}

export interface StockPreview {
  location_name?: string;
  locations_count?: number;
  comparison?: StockComparison[];
  matched: number;
  unchanged: number;
  changes: { sku: string; title: string; from: number | null; to: number }[];
  changes_total: number;
  unmatched: string[];
  unmatched_total: number;
  skipped_total?: number;
}

export interface ErpItem { sku: string; name: string; quantity: number }

export interface StockRunSummary { updated: number; unchanged: number; failed: number; unmatched: number; skipped?: number; pending: number }
export interface OrdersRunSummary { checked?: number; handled: number; note?: string }

export function describeOrdersRun(s: OrdersRunSummary): string {
  if (s.note === 'first_run') return 'הממשק הופעל עכשיו — מכאן והלאה כל הזמנה חדשה תיקלט';
  if (!s.checked) return 'אין הזמנות חדשות';
  return `נבדקו ${s.checked} הזמנות · ${s.handled} נקלטו לטיפול (הופקו או ממתינות לאישורך)`;
}

type RunnerResult<T> = { ok: true; data: T } | { ok: false; error: string };

async function runner<T>(action: string, companyId: string, extra: Record<string, unknown>, pick: (d: Record<string, unknown>) => T | undefined): Promise<RunnerResult<T>> {
  const { data, error } = await supabase.functions.invoke('interfaces-runner', { body: { action, company_id: companyId, ...extra } });
  if (data?.ok) {
    const value = pick(data);
    if (value !== undefined) return { ok: true, data: value };
  }
  if (typeof data?.error === 'string') return { ok: false, error: data.error };
  return { ok: false, error: error ? 'לא הצלחנו להתחבר לשרת. נסו שוב בעוד רגע.' : 'הפעולה נכשלה. נסו שוב.' };
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

export type ConnectionPatch = Partial<Pick<InterfaceConnection,
  'store_provider' | 'erp_provider' | 'store_integration_id' | 'erp_integration_id' | 'log_mode' | 'log_retention_days'>>;

const CONNECTION_COLS = 'id, company_id, store_provider, erp_provider, store_integration_id, erp_integration_id, log_mode, log_retention_days, updated_at';
const FLOW_COLS = 'id, company_id, connection_id, flow_key, is_enabled, direction, options, updated_at';

export const interfacesService = {
  async getConnection(companyId: string): Promise<InterfaceConnection | null> {
    const { data, error } = await supabase
      .from('interface_connections')
      .select(CONNECTION_COLS)
      .eq('company_id', companyId)
      .maybeSingle();
    if (error) throw error;
    return data as InterfaceConnection | null;
  },

  async saveConnection(companyId: string, patch: ConnectionPatch): Promise<InterfaceConnection> {
    const { data, error } = await supabase
      .from('interface_connections')
      .upsert({ company_id: companyId, ...patch, updated_at: new Date().toISOString() }, { onConflict: 'company_id' })
      .select(CONNECTION_COLS)
      .single();
    if (error) throw error;
    return data as InterfaceConnection;
  },

  async listIntegrations(companyId: string): Promise<IntegrationSummary[]> {
    const { data, error } = await supabase
      .from('company_integrations')
      .select('id, provider_name, is_active, last_sync_at')
      .eq('company_id', companyId);
    if (error) throw error;
    return (data ?? []) as IntegrationSummary[];
  },

  async listFlows(connectionId: string): Promise<InterfaceFlow[]> {
    const { data, error } = await supabase
      .from('interface_flows')
      .select(FLOW_COLS)
      .eq('connection_id', connectionId);
    if (error) throw error;
    return (data ?? []) as InterfaceFlow[];
  },

  async saveFlow(flow: Pick<InterfaceFlow, 'company_id' | 'connection_id' | 'flow_key' | 'is_enabled' | 'direction' | 'options'>): Promise<InterfaceFlow> {
    const { data, error } = await supabase
      .from('interface_flows')
      .upsert({ ...flow, updated_at: new Date().toISOString() }, { onConflict: 'connection_id,flow_key' })
      .select(FLOW_COLS)
      .single();
    if (error) throw error;
    return data as InterfaceFlow;
  },

  async getWarehouses(companyId: string): Promise<{ list: ErpWarehouse[]; at: string | null }> {
    const { data, error } = await supabase
      .from('interface_connections')
      .select('erp_warehouses, erp_warehouses_at')
      .eq('company_id', companyId)
      .maybeSingle();
    if (error) throw error;
    const list = Array.isArray(data?.erp_warehouses) ? (data.erp_warehouses as ErpWarehouse[]) : [];
    return { list, at: data?.erp_warehouses_at ?? null };
  },

  refreshWarehouses(companyId: string) {
    return runner('refresh_warehouses', companyId, {}, d => (Array.isArray(d.warehouses) ? (d.warehouses as ErpWarehouse[]) : undefined));
  },

  listErpItems(companyId: string) {
    return runner('list_items', companyId, {}, d => (Array.isArray(d.items) ? (d.items as ErpItem[]) : undefined));
  },

  previewOrders(companyId: string, options: FlowOptionValues) {
    return runner('preview_orders', companyId, { options, count: 10 }, d => (Array.isArray(d.orders) ? (d.orders as OrderPreview[]) : undefined));
  },

  previewStock(companyId: string, options: FlowOptionValues) {
    return runner('preview_stock', companyId, { options }, d => (isObj(d.result) && Array.isArray(d.result.changes) ? (d.result as unknown as StockPreview) : undefined));
  },

  runStockNow(companyId: string) {
    return runner('run_stock', companyId, {}, d => (isObj(d.result) ? (d.result as unknown as StockRunSummary) : undefined));
  },

  runOrdersNow(companyId: string) {
    return runner('run_orders', companyId, {}, d => (isObj(d.result) && typeof d.result.handled === 'number' ? (d.result as unknown as OrdersRunSummary) : undefined));
  },

  refreshOrderStock(companyId: string, orderId: string) {
    return runner('order_stock', companyId, { order_id: orderId }, d => (isObj(d.snapshot) ? (d.snapshot as Record<string, Record<string, number>>) : undefined));
  },

  issueOrder(companyId: string, orderId: string, assignments: Record<string, number> | null) {
    return runner('issue_manual', companyId, { order_id: orderId, assignments }, d => (Array.isArray(d.issued) ? d.issued.length : undefined));
  },

  cancelOrder(companyId: string, orderId: string) {
    return runner('cancel_order', companyId, { order_id: orderId }, () => true);
  },

  async listOrders(companyId: string, statuses: InterfaceOrderStatus[]): Promise<InterfaceOrder[]> {
    const { data, error } = await supabase
      .from('interface_orders')
      .select('id, shopify_order_id, order_name, status, reason, order_payload, allocation, stock_snapshot, processed_at, created_at, interface_order_documents(id, storage_id, storage_name, doc_kind, external_number, external_url, amount, split_reason, status, error_message, created_at)')
      .eq('company_id', companyId)
      .in('status', statuses)
      .order('created_at', { ascending: false })
      .limit(100);
    if (error) throw error;
    return (data ?? []) as InterfaceOrder[];
  },

  async listActivity(
    companyId: string,
    filters: { status?: ActivityLogEntry['status']; flowKey?: string },
  ): Promise<ActivityLogEntry[]> {
    let q = supabase
      .from('interface_activity_log')
      .select('id, flow_key, direction, status, source_ref, target_ref, summary, error_message, attempts, created_at')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(200);
    if (filters.status) q = q.eq('status', filters.status);
    if (filters.flowKey) q = q.eq('flow_key', filters.flowKey);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as ActivityLogEntry[];
  },
};

/** Picks the best company_integrations row for a provider: active first. */
export function findIntegration(
  integrations: IntegrationSummary[],
  providerNames: string[],
): IntegrationSummary | undefined {
  const matches = integrations.filter(i => providerNames.includes(i.provider_name));
  return matches.find(i => i.is_active) ?? matches[0];
}
